import uniqBy from "lodash/uniqBy";

import { QUESTION_TYPE } from "src/@types/Exam";

import { errorHandler, successHandler } from "./responseHandler";

export const flatObject = (array) => {
  let result = [];
  array.forEach((entity) => {
    result.push(entity);
    if (Array.isArray(entity?.children)) {
      result = result.concat(flatObject(entity?.children));
    }
  });
  return result;
};

export const tryCatchProtectedCall = async (
  apiCall,
  callbacks,
  errorCallbacks,
  loader
) => {
  try {
    if (loader) {
      loader(true);
    }
    const response = await apiCall();
    if (callbacks && [200, 201, 202]?.includes(response?.code)) {
      callbacks();
      successHandler(response);
    } else if (errorCallbacks) {
      errorCallbacks();
    }
  } catch (e) {
    errorHandler(e);
  } finally {
    if (loader) {
      loader(false);
    }
  }
};

export const parentSearchReccurssion = (results, allCategories) => {
  if (results?.length) {
    const parents = [];
    results?.forEach((res) => {
      const pars = allCategories?.filter((tag) => tag?.id === res?.parent);
      parents.push(res, ...pars);
    });
    return parents;
  }
  return [];
};

export const categorySearcher = (searchQuery, setter, allCategories) => {
  const results = allCategories?.filter((tag) =>
    tag?.text?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  const level4 = parentSearchReccurssion(results, allCategories);
  const level3 = parentSearchReccurssion(level4, allCategories);
  const level2 = parentSearchReccurssion(level3, allCategories);
  const level1 = parentSearchReccurssion(level2, allCategories);

  let hierarchy = [...level1, ...level2, ...level3, ...level4];
  hierarchy = uniqBy(hierarchy, "id");

  setter(hierarchy);

  // Reset to actual categories
  if (searchQuery?.length === 0) {
    setter(allCategories);
  }
};

export const getFileType = (lectureUrl) => {
  const extension = lectureUrl.split(".").pop();

  switch (extension) {
    case "mp4":
    case "avi":
    case "mov":
    case "mkv":
      return "video";
    case "mp3":
    case "wav":
      return "audio";
    case "doc":
    case "docx":
    case "pdf":
    case "ppt":
    case "pptx":
    case "xls":
    case "xlsx":
      return "document";
    default:
      return "unknown";
  }
};

export const isValueNegative = (value) => Math.sign(value) === -1;

export const indicateNumberSign = (value) => {
  if (value && isValueNegative(value)) {
    value = value?.replace("-", "");
    return `(${value}%)`;
  }
  return `${value || 0}%`;
};

export const containsHTML = (str: string) => {
  const htmlRegex = /<[^>]*>/g;
  return htmlRegex.test(str);
};

export const toLetters = (num: number): string => {
  const mod = num % 26;
  let pow = (num / 26) | 0;
  const out = mod ? String.fromCharCode(64 + mod) : (--pow, "Z");
  return pow ? toLetters(pow) + out : out;
};

export const isURL = (str: string | number) => {
  try {
    if (typeof str !== "string") {
      return false;
    }

    new URL(str);
    return true;
  } catch (error) {
    return false;
  }
};

export const getFileName = (url: string) => {
  try {
    return url.substring(url.lastIndexOf("/") + 1);
  } catch (error) {
    console.error("Invalid URL:", error);
    return "-";
  }
};

export const isHTML = (str: string): boolean => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(str, "text/html");
  return Array.from(doc.body.childNodes).some((node) => node.nodeType === 1);
};

export const generateQuestionsHtml = (questions, t) => {
  return `
  <html>
    <head>
      <style>
        body {
          font-family: 'Helvetica Neue', 'Helvetica', Helvetica, 'Arial', sans-serif;
          padding: 0.8in;
          font-size: 12px;
          color: #333;
        }
        .header {
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 20px;
          border-bottom: 1px solid #ccc;
          padding-bottom: 10px;
        }
        .footer {
          position: fixed;
          bottom: 0;
          text-align: center;
          width: 100%;
          font-size: 10px;
          color: #555;
        }
        .question {
          margin-bottom: 20px;
          padding: 15px;
          border-radius: 5px;
        }
        .question-number {
          font-weight: bold;
          margin-bottom: 8px;
          font-size: 14px;
        }
        .question-text {
          margin-bottom: 10px;
        }
        .options {
          margin-left: 20px;
        }
        .option {
          margin-bottom: 5px;
          padding: 5px 8px;
          background-color: #fff;
          border-left: 3px solid #ff6b4a;
        }
        .correct {
          border-left: 3px solid rgb(52, 188, 102);
        }
      </style>
    </head>
    <body>
      <div class="header">
        <h2>${t(
          "questions.bulkQuestionGeneration.steps.download.stemetaAi"
        )} - ${t(
    "questions.bulkQuestionGeneration.steps.download.generatedQuestion"
  )}</h2>
      </div>

      <div class="content">
        ${questions
          .map(
            (q, i) => `
          <div class="question">
            <div class="question-number">${t("questions.question")} ${
              i + 1
            }:</div>
            <div class="question-text">${q.questionText}</div>
            ${
              q?.options?.length
                ? `<div class="options">
                  ${q?.options
                    .map(
                      ({ name, value, isCorrectAnswer }) =>
                        `<div class="option ${
                          isCorrectAnswer ? "correct" : ""
                        }">${name}. ${value}</div>`
                    )
                    .join("")}
                </div>`
                : ""
            }
          </div>
        `
          )
          .join("")}
      </div>
    </body>
  </html>
`;
};

export const QUESTION_TYPE_SETS = {
  NEXT_GEN: [
    QUESTION_TYPE.MCQ,
    QUESTION_TYPE.PICTORIAL,
    QUESTION_TYPE.FILL_IN_THE_BLANK,
    QUESTION_TYPE.BOW_TIE,
    QUESTION_TYPE.MATRIX,
    QUESTION_TYPE.FILL_IN_THE_BLANK_TEXTUAL,
    QUESTION_TYPE.FILL_IN_THE_BLANK_FROM_LIST,
    QUESTION_TYPE.FINDINGS,
    QUESTION_TYPE.HIGHLIGHTED,
    QUESTION_TYPE.CASE_STUDY,
  ],
  TRADITIONAL: [
    QUESTION_TYPE.MCQ,
    QUESTION_TYPE.DESCRIPTIVE,
    QUESTION_TYPE.PICTORIAL,
  ],
};

export const ASSIGNMENT_TYPES = {
  NEXT_GEN: 4,
};

export const getAllowedQuestionTypesForAssessments = (
  assessmentType: number
) => {
  if (assessmentType === ASSIGNMENT_TYPES.NEXT_GEN) {
    return QUESTION_TYPE_SETS.NEXT_GEN;
  } else {
    return QUESTION_TYPE_SETS.TRADITIONAL;
  }
};

export const shuffleArray = (array) => {
  if (!Array.isArray(array)) return [];

  const copy = [...array];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
};

export const hasRealContent = (html: string) => {
  if (!html) return false;

  // Remove &nbsp;, <br>, whitespace, and empty tags
  const cleaned = html
    .replace(/&nbsp;/gi, "")
    .replace(/<br\s*\/?>/gi, "")
    .replace(/<p>\s*<\/p>/gi, "")
    .replace(/\s+/g, "")
    .trim();

  // Strip all tags and see if there's actual text left
  const textOnly = cleaned.replace(/<[^>]*>/g, "").trim();

  return textOnly.length > 0;
};

export const parseAnswers = (input) => {
  if (!input) return [];

  // handle case: input is an array of strings or a single string
  const items = Array.isArray(input) ? input : [input];

  const results = [];

  for (const item of items) {
    // Split by ";id:" to handle multiple blank entries in the same string
    // Format: "id: <blankId>, answer: <answers>;id: <blankId>, answer: <answers>;..."
    const segments = String(item).split(/;\s*id:/i);

    for (const segment of segments) {
      // For the first segment, it starts with "id:", for others it doesn't have the prefix
      const segmentToParse = segment.trim().startsWith("id:")
        ? segment.trim()
        : `id:${segment.trim()}`;

      // regex matches: id: <something>, answer: <anything>
      const match = segmentToParse.match(/id:\s*([^,]+),\s*answer:\s*(.+)/i);
      if (!match) continue;

      const [, id, answersStr] = match;

      // Clean and process answers
      const answers = answersStr
        .split(",") // split multiple answers
        .map((a) => {
          // Clean up malformed entries
          return String(a)
            .trim() // trim spaces first
            .split(";")[0] // Remove anything after semicolon (e.g., ";id: ...")
            .replace(/^answer:\s*/i, "") // Remove "answer:" prefix if present
            .trim(); // trim again after cleaning
        })
        .filter(Boolean); // remove empties

      results.push({
        id: id.trim(),
        answers,
      });
    }
  }

  return results;
};
